import React from "react";
import { Box, Typography, Paper, Container, Grid } from "@mui/material";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import AppleIcon from "@mui/icons-material/Apple";

const Download: React.FC = () => {
  return (
    <>
      {/* React 19 built-in metadata */}
      <title>Download ACTT Study App</title>
      <meta name="description" content="Download the ACTT Study mobile app for iOS and Android. Participate in mosquito surveillance and contribute to public health research on the go." />
      <meta property="og:title" content="Download ACTT Study App" />
      <meta property="og:description" content="Get the ACTT Study mobile app to participate in mosquito surveillance and contribute to public health research." />
      <link rel="canonical" href="https://acttstudy.org/download" />

    <Container maxWidth="lg">
      <Box sx={{ padding: 4, textAlign: "center" }}>
        <Typography
          variant="h3"
          gutterBottom
          sx={{ fontWeight: "bold", mb: 2 }}
        >
          Download the ACTT Study App
        </Typography>

        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: 700, mx: "auto" }}
        >
          Get the ACTT Study mobile app to participate in mosquito surveillance
          and contribute to public health research on the go.
        </Typography>

        <Grid container spacing={4} justifyContent="center" sx={{ mb: 8 }}>
          {/* iOS Card */}
          <Grid size={{ xs: 12, sm: 6, md: 5 }}>
            <Paper
              elevation={4}
              sx={{
                padding: 4,
                minHeight: 320,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: 6,
                },
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <AppleIcon sx={{ fontSize: 80, color: "#000", mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
                  iOS
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Available for iPhone and iPad
                </Typography>
              </Box>
              <a
                href="https://apps.apple.com/us/app/actt-study/id6744712322"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <img
                  src="/badges/app-store.svg"
                  alt="Download on the App Store"
                  style={{ height: "50px", cursor: "pointer" }}
                />
              </a>
            </Paper>
          </Grid>

          {/* Android Card */}
          <Grid size={{ xs: 12, sm: 6, md: 5 }}>
            <Paper
              elevation={4}
              sx={{
                padding: 4,
                minHeight: 320,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: 6,
                },
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <PhoneAndroidIcon sx={{ fontSize: 80, color: "#3DDC84", mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
                  Android
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Available for Android devices
                </Typography>
              </Box>
              <a
                href="https://play.google.com/store/apps/details?id=org.acttstudy.actt"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <img
                  src="/badges/google-play.svg"
                  alt="Get it on Google Play"
                  style={{ height: "50px", cursor: "pointer" }}
                />
              </a>
            </Paper>
          </Grid>
        </Grid>

        {/* Features Section */}
        <Paper
          elevation={2}
          sx={{
            padding: 4,
            mt: 6,
            backgroundColor: "#f8f9fa",
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
            App Features
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" gutterBottom>
                📸 Camera Integration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Capture and submit mosquito trap images directly from your
                mobile device
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" gutterBottom>
                🤖 AI-Powered Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Automatic egg counting using advanced machine learning
                algorithms
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h6" gutterBottom>
                📊 Track Your Data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage your submissions with detailed records and
                analytics
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* System Requirements */}
        <Paper
          elevation={2}
          sx={{
            mt: 6,
            mb: 4,
            padding: 4,
            maxWidth: 800,
            mx: "auto",
            backgroundColor: "#fff",
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontWeight: "bold", textAlign: "center", mb: 3 }}
          >
            System Requirements
          </Typography>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  padding: 3,
                  borderRadius: 2,
                  backgroundColor: "#f8f9fa",
                  textAlign: "center",
                }}
              >
                <AppleIcon sx={{ fontSize: 40, color: "#000", mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  iOS
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Requires iOS 13.0 or later
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Compatible with iPhone and iPad
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  padding: 3,
                  borderRadius: 2,
                  backgroundColor: "#f8f9fa",
                  textAlign: "center",
                }}
              >
                <PhoneAndroidIcon sx={{ fontSize: 40, color: "#3DDC84", mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  Android
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Requires Android 8.0 or later
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Available on Google Play
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
    </>
  );
};

export default Download;
